import { create } from 'zustand';
import { tavusPersonaApi } from '../api';
import { toast } from 'react-toastify';
import {
  EIntegrationSettingsType,
  EPersonaIntegrationMode,
  EPersonaViewMode,
  ICreateTavusPersonaReq,
  ICreateTavusPersonaRes,
  IIntegrationSettings,
  ITavusPersona,
  ITavusPersonaFormik
} from './tavusPersonaTypes';
import { showError } from 'shared/model';
import { getErrorMessageV2, logger } from 'shared/lib';

export const tavusInitialPersona: ITavusPersonaFormik = {
  name: '',
  defaultReplicaId: '',
  isRecording: false,
  context: '',
  systemPrompt: '',
  model: 'llama',
  custom_background: '',
  customGreeting: '',
  timer_config: { duration: 0, events: [] },
  preconversationConfig: {
    fields: []
  },
  voiceId: '',
  personaViewMode: EPersonaViewMode.ON_CALL,
  personaIntegrationViewMode: EPersonaIntegrationMode.EMBED,
  defaultLanguage: 'english',
  perceptionModel: 'off',
  ambientAwarenessQueries: [],
  widget: {
    second_button_text: 'Learn more',
    bubble_icon_variant: 'first',
    welcome_button_color: '#C34DFF',
    bubble_icon_side: 'left',
    custom_widget_trigger: '',
    bubble_icon_custom: false,
    bubble_icon_url: '',
    dark_mode: false,
    bubble_background_color: '#C34DFF',
    bubble_icon_color: '#fff',
    video_agent_name: 'Video agent',
    is_form_enabled: false,
    description: 'Ready to Meet Your AI Assistant?',
    button_text: 'Start call',
    powered_by: false,
    main_color: '#C34DFF',
    more_personalised_colors: false,
    font: 'Inter',
    camera: false,
    language_selection: false,
    captions_by_default: false,
    domains: [''],
    type: EIntegrationSettingsType.WIDGET,
    api_key_id: '',
    screen_share: false,
    cta_primary_button_text: 'Start call',
    cta_secondary_button_text: 'Learn more',
    cta_secondary_button_link: '',
    video_call_ended_custom_message:
      'Sign up and use Yepic conversational video APIs free.',
    cta_primary_button_link: ''
  },
  embed: {
    second_button_text: 'Learn more',
    dark_mode: false,
    video_agent_name: 'Video agent',
    is_form_enabled: false,
    description: 'Ready to Meet Your AI Assistant?',
    button_text: 'Start call',
    powered_by: false,
    main_color: '#C34DFF',
    more_personalised_colors: false,
    font: 'Inter',
    camera: false,
    language_selection: false,
    captions_by_default: false,
    domains: [''],
    type: EIntegrationSettingsType.EMBED,
    api_key_id: '',
    screen_share: false,
    cta_primary_button_text: 'Start call',
    cta_secondary_button_text: 'Learn more',
    cta_secondary_button_link: '',
    video_call_ended_custom_message:
      'Sign up and use Yepic conversational video APIs free.',
    cta_primary_button_link: ''
  },
  timer_config_enabled: false
};

type State = {
  isPersonaCreating: boolean;
  currentPersona: ITavusPersona | null;
  containPreview: boolean;
  tavusPersonas: ITavusPersona[];
  tavusPersonasLoading: boolean;
  sideBarOpened: boolean;
  personaEmbedModal: {
    personaId: string;
    type: EIntegrationSettingsType;
  } | null;
  isPersonaDeleting: boolean;
  personaShareModal: ITavusPersona | null;
  updateIntegrationSettingsLoading: boolean;
  deleteIntegrationSettingsLoading: boolean;
  personaViewMode: EPersonaViewMode;
  personaIntegrationViewMode: EPersonaIntegrationMode;
};

type Actions = {
  setPersonaViewMode: (mode: EPersonaViewMode) => void;
  setSideBarOpened: (sideBarOpened: boolean) => void;
  setContainPreview: (containPreview: boolean) => void;
  setPersonaIntegrationViewMode: (mode: EPersonaIntegrationMode) => void;
  addPersona: (
    data: ICreateTavusPersonaReq
  ) => Promise<ICreateTavusPersonaRes | undefined>;
  fetchPersonas: () => Promise<void>;
  fetchOne: (
    id: string,
    showErrorToast?: boolean
  ) => Promise<ITavusPersona | undefined>;
  setCurrentPersona: (persona: ITavusPersona | null) => void;
  togglePersonaEmbedModal: (
    personaId: string | null,
    type?: EIntegrationSettingsType
  ) => void;
  deletePersona: (id: string) => Promise<void>;
  updatePersona: (
    id: string,
    personaData: ICreateTavusPersonaReq
  ) => Promise<void>;
  setPersonaShareModal: (persona: ITavusPersona | null) => void;
  updateIntegrationSettings: (
    personaId: string,
    data: IIntegrationSettings
  ) => Promise<void>;
  deleteIntegrationSettings: (
    personaId: string,
    type: EIntegrationSettingsType
  ) => Promise<void>;
  duplicatePersona: (personaId: string) => Promise<void>;
};

type Store = State & Actions;

export const tavusPersonaStore = create<Store>((set, get) => ({
  currentPersona: null,
  // persona: null,
  tavusPersonas: [],
  sideBarOpened: false,
  tavusPersonasLoading: false,
  personaEmbedModal: null,
  isPersonaCreating: false,
  isPersonaDeleting: false,
  personaShareModal: null,
  updateIntegrationSettingsLoading: false,
  deleteIntegrationSettingsLoading: false,
  containPreview: false,
  setContainPreview: (containPreview) => {
    set({ containPreview });
  },
  setPersonaShareModal: (persona) => {
    set({ personaShareModal: persona });
  },
  setSideBarOpened: (sideBarOpened) => {
    set({ sideBarOpened });
  },
  personaViewMode: EPersonaViewMode.BEFORE_CALL,
  personaIntegrationViewMode: EPersonaIntegrationMode.EMBED,
  setPersonaViewMode: (personaViewMode) => set({ personaViewMode }),
  setPersonaIntegrationViewMode: (personaIntegrationViewMode) =>
    set({ personaIntegrationViewMode }),
  addPersona: async (personaData) => {
    set({ isPersonaCreating: true });
    try {
      const data = await tavusPersonaApi.create(personaData);
      await get().fetchPersonas();
      return data;
    } catch (error) {
      const message = getErrorMessageV2(
        error,
        'There was a problem when we tried to create a persona. Please try again later.'
      );
      showError(message);
    } finally {
      set({ isPersonaCreating: false });
    }
  },
  updatePersona: async (id, personaData) => {
    set({ isPersonaCreating: true });
    try {
      await tavusPersonaApi.update(id, personaData);
      await get().fetchPersonas();
    } catch (error) {
      const message = getErrorMessageV2(
        error,
        'There was a problem when we tried to change a persona. Please try again later.',
        true
      );
      showError(
        'There was a problem when we tried to change a persona. Please try again later.'
      );
    } finally {
      set({ isPersonaCreating: false });
    }
  },
  deletePersona: async (id) => {
    set({ isPersonaDeleting: true });
    try {
      await tavusPersonaApi.delete(id);
      const tavusPersonas = [...get().tavusPersonas];
      const indexToDelete = tavusPersonas.findIndex((p) => p.id === id);
      tavusPersonas.splice(indexToDelete, 1);

      set({ tavusPersonas });
    } catch (error) {
      const message = getErrorMessageV2(
        error,
        'There was a problem when we tried to delete a persona. Please try again later.'
      );
      showError(message);
    } finally {
      set({ isPersonaDeleting: false });
    }
  },
  setCurrentPersona: (persona) => {
    set({ currentPersona: persona });
  },
  togglePersonaEmbedModal: (personaId, type) => {
    set({
      personaEmbedModal: personaId
        ? {
            personaId,
            type: type!
          }
        : null
    });
  },
  fetchPersonas: async () => {
    set({ tavusPersonasLoading: true });
    try {
      const tavusPersonas = await tavusPersonaApi.fetchAll();

      set({ tavusPersonas });
    } catch (error) {
      toast.error(
        'There was a problem when we tried to get personas list. Please try again later.'
      );
    } finally {
      set({ tavusPersonasLoading: false });
    }
  },
  fetchOne: async (id: string, showErrorToast = true) => {
    try {
      return await tavusPersonaApi.fetch(id);
    } catch (error) {
      if (showErrorToast) {
        toast.error(
          'There was a problem when we tried to get persona details. Please try again later.'
        );
      }
    }
  },
  updateIntegrationSettings: async (id, data) => {
    set({ updateIntegrationSettingsLoading: true });

    try {
      await tavusPersonaApi.updateIntegrationSettings(id, data);

      set((state) => {
        const personas = [...state.tavusPersonas];
        const currentPersona = personas.find((item) => item.id === id);

        if (!currentPersona) return {};

        currentPersona.integration_settings = currentPersona
          .integration_settings?.length
          ? currentPersona.integration_settings.map((item) =>
              item.type === data.type ? data : item
            )
          : [data];

        return {
          tavusPersonas: personas.map((item) =>
            item.id === id ? { ...currentPersona } : item
          )
        };
      });
    } catch (error) {
      toast.error(
        'There was a problem updating integration settings. Please try again later.'
      );
    } finally {
      set({ updateIntegrationSettingsLoading: false });
    }
  },
  deleteIntegrationSettings: async (personaId, type) => {
    set({ deleteIntegrationSettingsLoading: true });

    try {
      await tavusPersonaApi.deleteIntegrationSettings(personaId, type);

      set((state) => {
        const personas = [...state.tavusPersonas];
        const currentPersona = personas.find((item) => item.id === personaId);

        if (!currentPersona) return {};

        currentPersona.integration_settings =
          currentPersona.integration_settings?.filter(
            (item) => item.type !== type
          ) || [];

        return {
          tavusPersonas: personas.map((item) =>
            item.id === personaId ? { ...currentPersona } : item
          )
        };
      });
    } catch (error) {
      logger.error(error);
      toast.error(
        'There was a problem when we tried to delete integration settings. Please try again later.'
      );
    } finally {
      set({ deleteIntegrationSettingsLoading: false });
    }
  },
  duplicatePersona: async (personaId) => {
    set({
      tavusPersonasLoading: true
    });
    try {
      await tavusPersonaApi.duplicatePersona(personaId);
      toast.success('Persona duplicated successfully.');
      await get().fetchPersonas();
    } catch (error) {
      const message = getErrorMessageV2(
        error,
        'There was a problem when we tried to duplicate a persona. Please try again later.'
      );
      showError(message);
    } finally {
      set({
        tavusPersonasLoading: false
      });
    }
  }
}));
